import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { type GroupElement } from '@bynder-studio/render-core';
import { Form, Select, token } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useDesign from 'packages/pages/design/hooks/useDesign';
import useElement from 'packages/pages/design/hooks/useElement';
import useVariations from 'packages/pages/design/hooks/useVariations';
import { type SceneRow } from './utils';

const DES_SEARCH_INPUT_THRESHOLD = 10;
const DES_NESTING_BACKGROUND_COLORS = ['#F6F7F8', '#F0F2F4', '#EAEDF0', '#E4E8EC', '#DEE3E8', '#D8DEE4'];

type Props = {
    row: Extract<SceneRow, { kind: 'des-group' }>;
    isDisabled: boolean;
    children?: React.ReactNode;
};

const DynamicElementSelectionRow = ({ row, isDisabled, children }: Props) => {
    const { translate } = useTranslate();
    const { creativeModel } = useDesign();
    const { updateVariationElement } = useVariations();
    const { element: groupElement, model } = useElement<GroupElement>(row.id);
    const [searchValue, setSearchValue] = useState('');

    const sortedChildren = useMemo(
        () => [...(groupElement?.children || [])].sort((left, right) => right.renderOrder - left.renderOrder),
        [groupElement?.children],
    );

    const activeChild = useMemo(() => {
        const activeChildId = groupElement?.activeDropdownElementId ?? row.selectedChild?.id;
        const found = sortedChildren.find((child) => child.id === activeChildId);

        return found || sortedChildren[0] || null;
    }, [groupElement?.activeDropdownElementId, row.selectedChild?.id, sortedChildren]);

    const filteredChildren = useMemo(
        () =>
            sortedChildren.filter(
                (child) => !searchValue || child.name.toLowerCase().includes(searchValue.toLowerCase()),
            ),
        [searchValue, sortedChildren],
    );

    const shouldShowSearch = sortedChildren.length > DES_SEARCH_INPUT_THRESHOLD;
    const nestedBackgroundColor = row.depth
        ? DES_NESTING_BACKGROUND_COLORS[Math.min(row.depth, DES_NESTING_BACKGROUND_COLORS.length) - 1]
        : undefined;

    const updateCurrentElement = useCallback(
        (elementId: number, newValues) => {
            if (!model) {
                return;
            }

            model.updateElement(elementId, newValues);
            updateVariationElement(creativeModel.getCurrentPageId(), elementId, newValues);
        },
        [model, updateVariationElement, creativeModel],
    );

    const handleSelectionChange = useCallback(
        (childId: string | null) => {
            if (!model || !groupElement || childId === null) {
                return;
            }

            const nextChildId = Number(childId);

            if (nextChildId === activeChild?.id) {
                return;
            }

            updateCurrentElement(groupElement.id, { activeDropdownElementId: nextChildId });
        },
        [activeChild, groupElement, model, updateCurrentElement],
    );

    return (
        <InspectorSection style={{ backgroundColor: nestedBackgroundColor }}>
            <Form.Group>
                <Form.Label>{groupElement?.name || row.element.name}</Form.Label>
                <Select
                    value={activeChild ? String(activeChild.id) : null}
                    onChange={handleSelectionChange}
                    label={activeChild?.name ?? ''}
                    isDisabled={isDisabled || !sortedChildren.length}
                    aria-label={groupElement?.name || row.element.name}
                >
                    {shouldShowSearch && (
                        <Select.SearchInput
                            value={searchValue}
                            onChange={setSearchValue}
                            placeholder={translate('editor.sidebar.shots.group.des.search')}
                            aria-label={translate('editor.sidebar.shots.group.des.search')}
                        />
                    )}
                    {filteredChildren.length > 0 ? (
                        filteredChildren.map((child) => (
                            <Select.Item key={child.id} value={String(child.id)}>
                                {child.name}
                            </Select.Item>
                        ))
                    ) : (
                        <Select.Text>{translate('editor.sidebar.shots.group.des.noResults')}</Select.Text>
                    )}
                </Select>
            </Form.Group>
            {children}
        </InspectorSection>
    );
};

const InspectorSection = styled.div`
    padding-block-start: ${token.spacing3};
    padding-inline: ${token.spacing6};
    margin-inline: calc(${token.spacing6} * -1);
    margin-block-start: calc(${token.spacing3} * -1);
`;

export default DynamicElementSelectionRow;
